/**
 * src/components/voice/ActiveVoiceStrip.jsx — in-panel optimistic voice progress.
 *
 * Pins a compact LIVE row to the top of the active panel the moment a voice
 * note / upload starts, advancing Uploading → Transcribing → Classifying →
 * Ready right where the operator is looking — instead of only the detached
 * global top tray + bottom-right toast (CEO 2026-06-10: "no information in the
 * optimistic view of the cards… this feedback is important to keep the users
 * engaged and know that everything's fine").
 *
 * Pure READ consumer of the two EXISTING in-progress sources — no new pipeline,
 * no new DB, no extra polling:
 *   - live mic notes → useVoiceBreadcrumbs (optimistic row on `voice:record_stop`
 *     + realtime voice_records), scoped to {contextType, contextId}.
 *   - file uploads   → uploadStatusStore jobs, filtered by projectId.
 * The global ProcessingRecordingToast already runs useUploadJobTracker, so we
 * MUST NOT call it here (it would double-poll voice_uploads).
 *
 * TICKET-959 B2: displays voice note headline in the viewer's chosen language.
 */
import { useMemo, useState, useCallback, useSyncExternalStore } from 'react';
import { X } from 'lucide-react';
import useVoiceBreadcrumbs from '../../hooks/useVoiceBreadcrumbs';
import { getUploadJobs, subscribeUploadJobs, removeUploadJob } from '../../lib/voice/uploadStatusStore';
import { toVoiceStage, isTerminalStatus } from '../../lib/voice/voiceStage';
import { useT } from '../../lib/i18n/I18nContext';
import { useContentLanguage } from '../../lib/i18n/useContentLanguage';
import { useLocalizedRows } from '../../lib/i18n/useLocalizedRows';

export default function ActiveVoiceStrip({ contextType, contextId, onOpenHistory = null }) {
  const { t } = useT();
  const { lang } = useContentLanguage();
  // Live-mic path: optimistic + realtime voice_records for this scope.
  const { notes } = useVoiceBreadcrumbs({ contextType, contextId, limit: 5 });
  // Upload path: global jobs store (read-only — tracker runs in the toast).
  const jobs = useSyncExternalStore(subscribeUploadJobs, getUploadJobs);
  const [dismissed, setDismissed] = useState(() => new Set());

  // Localize voice note headlines — TICKET-959 B2
  const { rows: localizedNotes } = useLocalizedRows('voice_records', notes || [], lang);

  const items = useMemo(() => {
    const out = [];
    const seen = new Set();

    // 1) File-upload jobs scoped to the active project (any phase until dismissed,
    //    so a just-finished upload shows a ✓ until the operator clears it).
    for (const j of jobs) {
      if (!j?.id) continue;
      // Uploads are always project-scoped → only ever shown in a project panel
      // whose contextId matches. A context-less (general/Focus) strip shows none.
      // Header-button uploads store scopeType/scopeId rather than projectId
      // (VoiceFileUploadBtn sets scopeId not projectId), so accept either.
      // TICKET-CODEX-3391508414
      const jobContextId = j.projectId ?? j.scopeId;
      if (!contextId || jobContextId !== contextId) continue;
      if (dismissed.has(j.id) || seen.has(j.id)) continue;
      seen.add(j.id);
      out.push({ key: j.id, source: 'upload', status: j.phase, title: j.fileName, pct: j.pct, error: j.error, createdAt: j.createdAt });
    }

    // 2) Live mic notes — only while in-progress (optimistic, or not yet settled).
    //    Settled rows (ready/error already loaded) live in the History feed.
    //    Use localized headline if available.
    for (const n of notes) {
      const key = n?.idempotency_key || n?.id;
      if (!key || seen.has(key) || dismissed.has(key)) continue;
      if (!n.optimistic && isTerminalStatus(n.status)) continue;
      seen.add(key);
      const localized = localizedNotes.find(ln => ln.id === n.id) || n;
      out.push({ key, source: 'mic', status: n.status, title: localized.headline || '', error: null, createdAt: n.created_at });
    }

    return out.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [jobs, notes, localizedNotes, contextId, contextType, dismissed]);

  const dismiss = useCallback((item) => {
    setDismissed((prev) => { const next = new Set(prev); next.add(item.key); return next; });
    if (item.source === 'upload') removeUploadJob(item.key);
  }, []);

  if (items.length === 0) return null;

  return (
    <div data-testid="active-voice-strip" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map((item) => {
        const stage = toVoiceStage(item.status);
        const Icon = stage.Icon;
        const tone = stage.tone === 'ok' ? 'var(--ok)' : stage.tone === 'bad' ? 'var(--bad)' : 'var(--info)';
        const isReady = stage.key === 'ready';
        const isErr = stage.key === 'error';
        const showPct = item.source === 'upload' && stage.key === 'uploading' && typeof item.pct === 'number' && item.pct > 0;
        return (
          <div
            key={item.key}
            data-testid={`avs-row-${item.key}`}
            data-stage={stage.key}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, minHeight: 44,
              padding: '8px 14px', borderRadius: 10,
              background: 'var(--glass)', border: '1px solid var(--bdr)',
            }}
          >
            <Icon size={15} color={tone} className={stage.spin ? 'animate-spin' : ''} style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.title || t('upload.processingTitle')}
              </div>
              <div style={{ fontSize: 11, color: tone, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {isErr ? (item.error || t(stage.i18nKey)) : t(stage.i18nKey)}
              </div>
            </div>

            {showPct && (
              <progress
                data-testid={`avs-pct-${item.key}`}
                value={item.pct}
                max={100}
                aria-label={t('upload.step.uploading')}
                style={{ width: 56, height: 4, border: 'none', borderRadius: 2, appearance: 'none', WebkitAppearance: 'none', backgroundColor: 'var(--info-tint)', color: 'var(--info)', overflow: 'hidden' }}
              />
            )}

            {isReady && onOpenHistory && (
              <button
                data-testid={`avs-open-${item.key}`}
                onClick={() => { onOpenHistory(item); dismiss(item); }}
                style={{ minHeight: 44, padding: '6px 12px', border: 'none', background: 'transparent', color: 'var(--info)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                {t('upload.openInHistory')}
              </button>
            )}

            <button
              data-testid={`avs-dismiss-${item.key}`}
              onClick={() => dismiss(item)}
              aria-label={t('common.dismiss')}
              style={{ minWidth: 44, minHeight: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', color: 'var(--tm)', cursor: 'pointer', flexShrink: 0 }}
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
